// ── Haptic Feedback ──
// Uses navigator.vibrate() API for tactile feedback on mobile devices.
// Each game event has a characteristic vibration pattern.

import { settingsStore } from '../core/settings';

/** Vibration patterns for each game event (durations in ms). */
const PATTERNS = {
  fire:          [15],
  hitConfirm:    [30, 20, 30],
  headshot:      [50, 30, 80],
  grenadeThrow:  [20, 10, 40],
  takeDamage:    [40],
  lowHealth:     [10, 50, 10, 50, 10],
  killConfirmed: [30, 20, 50, 20, 80],
  death:         [100, 50, 200],
} as const;

type HapticsSettingShape = boolean | {
  enabled?: boolean;
  strength?: number;
};

function readHapticsSettings(): { enabled: boolean; strength: number } {
  let enabled = true;
  let strength = 1.0;

  try {
    const settings = settingsStore.get() as unknown as { haptics?: HapticsSettingShape };
    const haptics = settings.haptics;
    if (typeof haptics === 'boolean') {
      enabled = haptics;
    } else if (haptics) {
      if (typeof haptics.enabled === 'boolean') enabled = haptics.enabled;
      if (typeof haptics.strength === 'number') {
        strength = Math.max(0, Math.min(1, haptics.strength));
      }
    }
  } catch {
    // Settings can be unavailable in stripped-down test harnesses.
  }

  return { enabled, strength };
}

export class HapticFeedback {
  private _enabled = true;
  private _strength = 1.0; // 0-1 multiplier

  /** Whether the Vibration API is available on this device. */
  isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'vibrate' in navigator && typeof navigator.vibrate === 'function';
  }

  /** Fire weapon — light tap. */
  fire(): void {
    this._vibrate(PATTERNS.fire);
  }

  /** Hit confirmed (damage dealt) — double pulse. */
  hitConfirm(): void {
    this._vibrate(PATTERNS.hitConfirm);
  }

  /** Headshot — strong pulse. */
  headshot(): void {
    this._vibrate(PATTERNS.headshot);
  }

  /** Grenade throw — medium pulse. */
  grenadeThrow(): void {
    this._vibrate(PATTERNS.grenadeThrow);
  }

  /** Take damage — medium tap. */
  takeDamage(): void {
    this._vibrate(PATTERNS.takeDamage);
  }

  /** Low health warning — rhythmic pattern. */
  lowHealth(): void {
    this._vibrate(PATTERNS.lowHealth);
  }

  /** Kill confirmed — escalating pattern. */
  killConfirmed(): void {
    this._vibrate(PATTERNS.killConfirmed);
  }

  /** Death — heavy pattern. */
  death(): void {
    this._vibrate(PATTERNS.death);
  }

  /** Play a custom vibration pattern. */
  custom(pattern: number[]): void {
    this._vibrate(pattern);
  }

  /** Enable or disable haptic feedback. */
  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
  }

  /**
   * Set the strength multiplier (0-1).
   * Scales all vibration durations. Values outside 0-1 are clamped.
   */
  setStrength(strength: number): void {
    this._strength = Math.max(0, Math.min(1, strength));
  }

  /** Apply strength scaling and vibrate. */
  private _vibrate(pattern: readonly number[] | number[]): void {
    if (!this._enabled) return;
    const settings = readHapticsSettings();
    if (!settings.enabled) return;
    if (!this.isSupported()) return;
    const strength = this._strength * settings.strength;
    // Strength of 0 means no vibration at all — skip the call entirely
    // rather than issuing 1ms buzzes with preserved pauses.
    if (strength <= 0) return;

    // navigator.vibrate pattern: even indices are vibration durations,
    // odd indices are pause durations. Only scale the vibrations — scaling
    // pauses distorts the rhythm of the pattern.
    const scaled = pattern.map((d, i) => {
      if (i % 2 === 1) return d; // pause — preserve duration
      return Math.max(1, Math.round(d * strength));
    });

    try {
      navigator.vibrate(scaled);
    } catch {
      // Silently ignore — vibration may fail on some devices
    }
  }
}
